import { Divider, Form, FormInstance, Select } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Company } from '../../../types/company/Company'
import { User } from '../../../types/user/User'
import { DeliverySubscriptionFormData, InitiatorType } from '../types'
import InitiatorFields from './InitiatorFields'

const { Option } = Select

interface SharedSubscriptionFieldsProps {
  form: FormInstance<DeliverySubscriptionFormData>
  initiatorOptions: readonly InitiatorType[]
  selectableCompanies: Company[]
  selectableUsers: User[]
  showRecipients?: boolean
}

const SharedSubscriptionFields: React.FC<SharedSubscriptionFieldsProps> = ({
  form,
  initiatorOptions,
  selectableCompanies,
  selectableUsers,
  showRecipients = false
}) => {
  const { t } = useTranslation()

  return (
    <>
      <Form.Item
        name="companyId"
        label={t('subscriptionsPage:company')}
        rules={[{ required: true, message: t('global:required-field') }]}
      >
        <Select placeholder={t('subscriptionsPage:select-company')} showSearch optionFilterProp="children">
          {selectableCompanies.map((company: Company) => (
            <Option key={company.id} value={company.id}>
              {company.name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item shouldUpdate noStyle>
        {() => {
          const hasRecipients = form.getFieldValue('recipients')?.length > 0
          const hasUserId = !!form.getFieldValue('userId')
          return (
            <>
              {!hasRecipients && (
                <Form.Item
                  label={t('subscriptionsPage:user')}
                  name="userId"
                  shouldUpdate
                  rules={[{ required: true, message: t('global:required-field') }]}
                >
                  <Select
                    placeholder={t('subscriptionsPage:select-user')}
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    onChange={(value: number) => {
                      if (value) form.setFieldsValue({ recipients: undefined })
                    }}
                  >
                    {selectableUsers.map((user: User) => (
                      <Option key={user.id} value={user.id}>
                        {user.displayName}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
              {showRecipients && !hasRecipients && !hasUserId && <Divider>{t('global:or')}</Divider>}
              {showRecipients && (hasRecipients || !hasUserId) && (
                <Form.Item
                  shouldUpdate
                  label={t('subscriptionsPage:recipients')}
                  name="recipients"
                  rules={[{ required: true, message: t('global:required-field') }]}
                >
                  <Select
                    mode="tags"
                    optionFilterProp="children"
                    onChange={(values: string[]) => {
                      if (values.length > 0) form.setFieldsValue({ userId: undefined })
                    }}
                  />
                </Form.Item>
              )}
            </>
          )
        }}
      </Form.Item>

      <InitiatorFields options={initiatorOptions} />
    </>
  )
}

export default SharedSubscriptionFields
